import { cn } from 'components/UI/cn'
import React, { ButtonHTMLAttributes, DetailedHTMLProps } from 'react'
import { Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { Tooltip } from 'components/Tooltip/tooltip'

export type FbButtonVariants =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'danger'
  | 'danger-outline'
  | 'transparent'
  | 'outline'
  | 'primary-outline'

export type FbButtonSizes = 'sm' | 'md' | 'lg' | 'xs' | 'mdlg'

export interface IFbButtonProps extends Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  'ref'
> {
  loading?: boolean
  variant?: FbButtonVariants
  size?: FbButtonSizes
  tooltip?: string
}

const FbButton = React.forwardRef((props: IFbButtonProps, ref: any) => {
  const { className, loading, variant, disabled, tooltip, ...rest } = props
  const size = props.size ?? 'md'

  return (
    <Tooltip delay={[0, 0]} content={tooltip}>
      <button
        ref={ref}
        className={cn(
          'flex-shrink-0 min-w-0 text-base rounded-md font-medium min-h-10 px-3 flex items-center text-center gap-2 justify-center disabled:border-transparent',
          // Primary variant (default)
          'bg-[#06512F] text-white hover:bg-[#2C6B4E] hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)] active:bg-[#054628] active:shadow-[inset_0px_4px_4px_-1px_rgba(12,12,13,0.05)]',
          {
            // Secondary variant
            '!bg-white text-[#6E6E6E] !border !border-[#E2E2E2] hover:!bg-white hover:text-[#6E6E6E] hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)] active:!bg-[#FAFAFA] active:!border-[#6E6E6E] active:text-[#191D23] active:shadow-none':
              variant === 'secondary',
            'bg-[#FDB81E] text-black hover:bg-[#FDB81E]': variant === 'warning',
            'bg-red-600 text-white hover:bg-red-700': variant === 'danger',
            'bg-transparent border-solid border-1 border-red-400 text-red-400 hover:text-red-400':
              variant === 'danger-outline',
            'bg-transparent border-solid border-0 hover:shadow-none text-black':
              variant === 'transparent',
            'bg-transparent border-solid text-black border-gray-200 border-[1px]':
              variant === 'outline',
            'bg-new-company-pistachio-light border-solid border-fb-dark-green text-fb-dark-green border-[1px] hover:bg-new-company-pistachio-medium':
              variant === 'primary-outline',
            'bg-new-company-gray-border text-new-company-gray-500 hover:text-new-company-gray-500 hover:bg-new-company-gray-border hover:shadow-none':
              disabled,
            'min-h-14': size === 'lg',
            'min-h-12': size === 'mdlg',
            'min-h-10': size === 'md',
            'min-h-8 text-sm': size === 'sm',
            'min-h-6 text-xs px-2': size === 'xs',
          },
          className
        )}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <div className="flex items-center w-full justify-center">
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          </div>
        ) : (
          <>{props.children}</>
        )}
      </button>
    </Tooltip>
  )
})
FbButton.displayName = 'FbButton'

export default FbButton

export const IconMiniButton = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  border-radius: 8px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
